<template>
    <div class="doc-table">
        <el-table :data="documentShareArray" style="width: 100%" empty-text="No document shares" row-key="id">
            <el-table-column label="Document Shares">
                <template #default="scope">
                    <div class="doc-table__row">
                        <div class="doc-table__row-top">
                            <div class="doc-table__privacy">
                                <el-tag type="primary">
                                    {{accessLevelDescriptions.get(scope.row.accessLevel)}}
                                </el-tag>
                            </div>
                            <span class="doc-table__accessLevel">
                                <a :href="scope.row.url" target="_blank" class="doc-table__link">
                                    {{ scope.row.title }}
                                </a>
                            </span>

                            <div class="doc-table__actions" v-if="isAdmin">
                                <el-button type="primary" @click="openEditForm(scope.row)">
                                    Edit
                                </el-button>
                                <el-button plain type="danger" @click="handleDeleteDocumentShare(scope.row)">
                                    Delete
                                </el-button>
                            </div>
                        </div>

                        <div class="doc-table__comment">
                            {{ scope.row.comment || 'No comments' }}
                        </div>
                    </div>
                </template>
            </el-table-column>
        </el-table>

        <!-- Edit Dialog -->
        <el-dialog v-model="isEditDialogVisible" title="Edit Document Share">
            <el-form :model="editForm">
                <el-form-item label="Title">
                    <el-input v-model="editForm.title" />
                </el-form-item>
                <el-form-item label="URL">
                    <el-input v-model="editForm.url" />
                </el-form-item>
                <el-form-item label="Access Level">
                    <el-select v-model="editForm.accessLevel">
                        <el-option v-for="[key, value] in accessLevelDescriptions" :key="key" :label="value"
                            :value="key" />
                    </el-select>
                </el-form-item>
                <el-form-item label="Comments">
                    <el-input v-model="editForm.comment" type="textarea" rows="4"></el-input>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="isEditDialogVisible = false">Cancel</el-button>
                <el-button type="primary" @click="saveDocumentShare">Save</el-button>
            </template>
        </el-dialog>

        <div class="doc-table__footer" v-if="isAdmin">
            <el-button type="primary" @click="openAddForm">
                Add New
            </el-button>
        </div>

        <!-- Edit/Add Dialog -->
        <el-dialog v-model="isEditDialogVisible" :title="isEditing ? 'Edit Document Share' : 'Add New Document Share'">
            <el-form :model="editForm">
                <el-form-item label="Title">
                    <el-input v-model="editForm.title" />
                </el-form-item>
                <el-form-item label="URL">
                    <el-input v-model="editForm.url" />
                </el-form-item>
                <el-form-item label="Access Level">
                    <el-select v-model="editForm.accessLevel">
                        <el-option v-for="[key, value] in accessLevelDescriptions" :key="key" :label="value"
                            :value="key" />
                    </el-select>
                </el-form-item>
                <el-form-item label="Comments">
                    <el-input v-model="editForm.comment" type="textarea" rows="4"></el-input>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="isEditDialogVisible = false">Cancel</el-button>
                <el-button type="primary" @click="saveDocumentShare">Save</el-button>
            </template>
        </el-dialog>
        <!-- Delete Confirmation Dialog -->
        <el-dialog v-model="isDeleteDocumentShareConfirmationVisible" width="30%" title="Confirmation">
            <span v-if="documentShareToDelete">
                Are you sure you want to delete
                <b>{{ documentShareToDelete.title }}</b>?
            </span>
            <template #footer>
                <el-button @click="cancelDocumentShareDelete">Cancel</el-button>
                <el-button type="danger" @click="requestDeleteDocumentShare(documentShareToDelete.id)">
                    Delete
                </el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import {
    getTeamDocumentShares,
    removeTeamDocumentShare,
    addTeamDocumentShare,
    updateTeamDocumentShare,
} from '@/api/team.js';
import { showMessage } from '@/utils/messages.js';

export default {
    name: 'TeamDocuments',
    props: {
        teamId: {
            type: Number,
            required: true,
        },
        isAdmin: {
            type: Boolean,
            required: false,
        },
    },
    data() {
        return {
            documentShareArray: [],
            accessLevelDescriptions: new Map([
                [0, 'Private'],
                //[1, 'Team'],
                [2, 'Public'],
            ]),
            isEditDialogVisible: false,
            editForm: {},
            isDeleteDocumentShareConfirmationVisible: false,
            isEditing: false,
            documentShareToDelete: {},
        };
    },
    methods: {
        async fetchDocumentShares() {
            const { teamId } = this;
            const { data, success } = await getTeamDocumentShares({ teamId });

            if (success) {
                this.setData(data.items);
            } else {
                showMessage({ type: 'error', message: 'Failed to load documents' });
            }
        },
        setData(documentShares) {
            this.documentShareArray = documentShares.map((doc) => ({
                id: doc.id,
                title: doc.title,
                url: doc.url,
                accessLevel: doc.accessLevel,
                comment: doc.comment,
            }));
        },
        openEditForm(documentShare) {
            this.editForm = { ...documentShare };
            this.isEditing = true;
            this.isEditDialogVisible = true;
        },
        openAddForm() {
            this.editForm = {
                title: '',
                url: '',
                accessLevel: 0,
                comment: '',
            };
            this.isEditing = false;
            this.isEditDialogVisible = true;
        },
        async saveDocumentShare() {
            if (this.isEditing) {
                const index = this.documentShareArray.findIndex(
                    (doc) => doc.id === this.editForm.id
                );
                if (index !== -1) {
                    this.documentShareArray[index] = { ...this.editForm };
                    const { success } = await updateTeamDocumentShare({
                        ...this.editForm,
                    });
                    if( success) {
                        showMessage({
                            type: 'success',
                            message: 'Document updated successfully',
                        });
                    } else {
                        showMessage({
                            type: 'error',
                            message: 'Failed to update document',
                        });
                    }
                }
            } else {
                const { success } = await addTeamDocumentShare({
                    teamId: this.teamId,
                    ...this.editForm,
                });
                if (success) {
                    showMessage({
                        type: 'success',
                        message: 'Document added successfully',
                    });
                    this.fetchDocumentShares();
                } else {
                    showMessage({
                        type: 'error',
                        message: 'Failed to add document',
                    });
                }
            }
            this.isEditDialogVisible = false;
        },
        handleDeleteDocumentShare(documentShare) {
            this.documentShareToDelete = documentShare;
            this.isDeleteDocumentShareConfirmationVisible = true;
        },
        cancelDocumentShareDelete() {
            this.isDeleteDocumentShareConfirmationVisible = false;
            this.documentShareToDelete = {};
        },
        async requestDeleteDocumentShare(documentShareId) {
            const { success } = await removeTeamDocumentShare({ id: documentShareId });
            if (success) {
                showMessage({
                    type: 'success',
                    message: 'Document share deleted',
                });
                this.fetchDocumentShares();
            } else {
                showMessage({
                    type: 'error',
                    message: 'Failed to delete document',
                });
            }
            this.cancelDocumentShareDelete();
        },
    },
    created() {
        this.fetchDocumentShares();
    },
};
</script>

<style lang="scss">
.doc-table {
    padding: 20px;
    background-color: #fff;

    &__link {
        color: #409eff;
        text-decoration: none;
        margin-right: 10px;
        text-align: left;
    }

    &__row {
        margin-bottom: 20px;
    }

    &__row-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    &__accessLevel{
      font-size: 14px;
      text-align: left;
      color: #606266;
    }

    &__comment {
        font-size: 12px;
        color: #606266;
        margin-top: 10px;
    }

    &__footer {
        margin-top: 20px;
        text-align: right;
    }

    &__privacy {
    margin-right: var(--global-dimension-size-200);
    margin-bottom: var(--global-dimension-size-200);
    }
}
</style>